import { useEffect, useState } from "react"
import HandHold from "handhold-sdk"
import { HandholdWidget } from "handhold-react"
import { handholdApiKey, handholdBaseUrl } from "utils/constants"
import { useAuthStore, useSideNavigationStore } from "stores"
import { linkSideNavToHandhold } from "./sideNavHandoff"

const isConfigured = Boolean(handholdApiKey && handholdBaseUrl)

// The widget's stylesheet is Tailwind v4 and cannot go through this app's
// Tailwind v3 PostCSS pipeline, so it is copied into public/ at build time
// (scripts/copy-handhold-css.js) and linked here instead of imported.
const STYLESHEET_ID = "handhold-react-styles"

const ensureStylesheet = () => {
  if (document.getElementById(STYLESHEET_ID)) return
  const link = document.createElement("link")
  link.id = STYLESHEET_ID
  link.rel = "stylesheet"
  link.href = `${process.env.PUBLIC_URL}/vendor/handhold-react.css`
  document.head.appendChild(link)
}

// Usage analytics are broken down by PaidHR company on Handhold's dashboard.
// Only the numeric company id is sent: no names, emails or page content.
const currentCompanyRef = (companyId?: number | null) =>
  companyId != null ? String(companyId) : null

// The SDK is a singleton, so init runs once per page load no matter how many
// layouts mount this. Kept at module scope so StrictMode's double-effect and
// route changes reuse the same promise.
let initPromise: Promise<void> | null = null

// "Talk to a person" opens PaidHR's HubSpot support chat (useHubspotChat loads
// it on the live domain). Returning false tells the widget it isn't on the
// page, so the button falls back to Handhold's own menu.
const openSupportChat = () => {
  const hubspot = (window as any).HubSpotConversations
  if (!hubspot?.widget) return false
  if (hubspot.widget.status?.().loaded === false) hubspot.widget.load()
  hubspot.widget.open()
  return true
}

const initHandhold = () => {
  if (!initPromise) {
    initPromise = HandHold.init({
      apiKey: handholdApiKey as string,
      baseUrl: handholdBaseUrl as string,
      // The React widget renders its own trigger and step indicator.
      showHelpButton: false,
      showStepHighlight: false,
      deferRestore: true,
      // Creates the agent engine behind "Do it for me". Also needs the
      // tenant's agent feature flag and a reviewed site map server-side.
      agentMode: true,
      contactSupport: openSupportChat,
      analytics: {
        companyId: currentCompanyRef(useAuthStore.getState().user?.currentCompanyId),
      },
      // Payroll/HR data must not leave the browser in a screenshot. Structural
      // regions (payslips, compensation tab, payroll report details) are marked
      // `data-handhold-private` in the markup; these are the loose input fields.
      // Passwords, OTP and cc-* fields are masked by the SDK already.
      screenshotMask: [
        '[name="accountNumber"]',
        '[name="bvn"]',
        '[name="nin"]',
      ],
    }).catch((error) => {
      // Never let the help widget break the page it is helping with.
      initPromise = null
      console.error("HandHold init failed", error)
      throw error
    })
  }
  return initPromise
}

// Resume a guide that was in progress when the page loaded. Deliberately NOT
// chained into initPromise: the widget must mount whether or not there is a
// session to restore. When it was chained, a restore that waited on the user
// ("continue where you left off?") kept the widget from ever mounting, and with
// it the only place that question could be answered.
let restoreStarted = false

const restoreHandhold = () => {
  if (restoreStarted) return
  restoreStarted = true
  HandHold.restoreSession().catch((error) => {
    console.error("HandHold session restore failed", error)
  })
}

/**
 * Mounts the HandHold in-app guidance widget. Renders nothing until the SDK
 * has initialised, and nothing at all when the env vars are unset.
 */
const HandholdHelp = () => {
  const [isReady, setIsReady] = useState(false)
  const companyId = useAuthStore((state) => state.user?.currentCompanyId)

  useEffect(() => {
    if (!isConfigured) return

    let isStale = false
    ensureStylesheet()
    initHandhold()
      .then(() => {
        if (!isStale) setIsReady(true)
        restoreHandhold()
      })
      .catch(() => {
        // already logged in initHandhold
      })

    return () => {
      isStale = true
    }
  }, [])

  // Switching company re-tags usage events from then on.
  useEffect(() => {
    if (!isReady) return
    HandHold.setAnalyticsContext({ companyId: currentCompanyRef(companyId) })
  }, [isReady, companyId])

  // Panel open -> side navigation collapses; the user expanding it puts the
  // panel away. See sideNavHandoff for the rules.
  useEffect(() => {
    if (!isReady) return
    return linkSideNavToHandhold(HandHold, useSideNavigationStore)
  }, [isReady])

  if (!isConfigured || !isReady) return null

  return <HandholdWidget />
}

export default HandholdHelp
